import * as React from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import parse from "autosuggest-highlight/parse";
import { debounce } from "@mui/material/utils";
import KEYS from "../keys/google.json";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button"; // Button for "Peruuta"
import config from "../keys/config.json";
import { t } from "i18next";

// Script loader with Promise support
function loadGoogleMapsScript() {
  return new Promise((resolve, reject) => {
    // Already loaded
    if (window.google?.maps?.places) {
      resolve(window.google.maps);
      return;
    }

    // Already loading
    const existingScript = document.querySelector("#google-maps");
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(window.google.maps));
      existingScript.addEventListener("error", reject);
      return;
    }

    // Load new script
    const script = document.createElement("script");
    script.id = "google-maps";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${KEYS.GEOCODING}&libraries=places&language=${config.language}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google.maps);
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

const autocompleteService = { current: null };


export default function GoogleMaps({
  label,
  value,
  onChange,
  isMobile = false,
}) {
  const [inputValue, setInputValue] = React.useState("");
  const [options, setOptions] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [mapsLoaded, setMapsLoaded] = React.useState(
    () => !!window.google?.maps?.places
  );
  const [loadError, setLoadError] = React.useState(false);

  // Load Google Maps script on first interaction or mount
  React.useEffect(() => {
    if (!mapsLoaded) {
      loadGoogleMapsScript()
        .then(() => setMapsLoaded(true))
        .catch((err) => {
          console.error("Failed to load Google Maps:", err);
          setLoadError(true);
        });
    }
  }, [mapsLoaded]);

  const fetch = React.useMemo(
    () =>
      debounce((request, callback) => {
        if (autocompleteService.current) {
          autocompleteService.current.getPlacePredictions(
            { ...request, componentRestrictions: { country: config.country } },
            callback
          );
        }
      }, 400),
    []
  );

React.useEffect(() => {
    let active = true;

    // Initialize autocomplete service when Maps is loaded
    if (mapsLoaded && !autocompleteService.current && window.google?.maps?.places) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }

    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === "") {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch({ input: inputValue }, (results) => {
      if (active) {
        let newOptions = [];
        if (value) {
          newOptions = [value];
        }
        if (results) {
          newOptions = [...newOptions, ...results];
        }
        setOptions(newOptions);
      }
    });
    return () => {
      active = false;
    };
  }, [value, inputValue, fetch, mapsLoaded]);




  const handleOpen = () => {
    if (isMobile) {
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChange = React.useCallback((newValue) => {
    setOptions(newValue ? [newValue, ...options] : options);
    if (newValue) {
      onChange(newValue);
    } else {
      onChange(null);
    }
    handleClose();
  }, [options, onChange]);
  

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && options.length > 0) {
      handleChange(options[0]);
      setOpen(false); // Close the options list
    }
  };

  if (!mapsLoaded) {
    return (
      <TextField
        label={label}
        fullWidth
        disabled
        placeholder="Ladataan..."
        InputProps={{
          startAdornment: (
            <LocationOnIcon sx={{ color: "action.disabled", mr: 1 }} />
          ),
        }}
        sx={{
          marginBottom: "16px",
          "& .MuiOutlinedInput-root": { borderRadius: "8px" },
        }}
      />
    );
  }

  if (loadError) {
    return (
      <TextField
        label={label}
        fullWidth
        error
        helperText="Karttapalvelua ei voitu ladata"
        InputProps={{
          startAdornment: (
            <LocationOnIcon sx={{ color: "error.main", mr: 1 }} />
          ),
        }}
        sx={{
          marginBottom: "16px",
          "& .MuiOutlinedInput-root": { borderRadius: "8px" },
        }}
      />
    );
  }
  
  return (
    <>
      {isMobile ? (
        <>
          <TextField
            label={label}
            fullWidth
            onClick={handleOpen}
            InputProps={{
              startAdornment: (
                <LocationOnIcon sx={{ color: "action.active", mr: 1 }} />
              ),
            }}
            value={inputValue}
          />
          <Dialog
           open={open} 
           onClose={handleClose} 
           fullScreen
           disableAutoFocus={false}
           disableEnforceFocus={false}
           disableRestoreFocus={false}
           >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 2 }}>
              <Typography variant="h6">{label}</Typography>
              <Button onClick={handleClose} color="primary" sx={{ textTransform: 'none' }}>
                {t("Peruuta")}
              </Button>
            </Box>
            <DialogContent>
              <Autocomplete
                open
                sx={{
                  width: "100%",
                  marginBottom: "16px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  },
                }}
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : option.description
                }
                filterOptions={(x) => x}
                options={options}
                autoComplete
                includeInputInList
                filterSelectedOptions
                value={value}
                noOptionsText={t("noLocations")}
                onChange={(event, newValue) => handleChange(newValue)}
                onInputChange={(event, newInputValue) => {
                  setInputValue(newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    // inputRef={(el) => {
                    //   // Combine the autocomplete ref with our custom ref
                    //   if (params.InputProps.ref) {
                    //     params.InputProps.ref.current = el;
                    //   }
                    //   inputRef.current = el;
                    // }}
                    autoFocus // Keep this for initial attempt
                    onKeyDown={handleKeyDown} // Move the onKeyDown handler here
                    label={label}
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <LocationOnIcon
                          sx={{ color: "action.active", mr: 1 }}
                        />
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props;

                  const structuredFormatting =
                    option.structured_formatting || {};
                  const mainText = structuredFormatting.main_text || "";
                  const secondaryText =
                    structuredFormatting.secondary_text || "";
                  const matches =
                    structuredFormatting.main_text_matched_substrings || [];

                  const parts = parse(
                    mainText,
                    matches.map((match) => [
                      match.offset,
                      match.offset + match.length,
                    ])
                  );

                  return (
                    <li key={key} {...optionProps}>
                      <Grid container alignItems="center">
                        <Grid item sx={{ display: "flex", width: 44 }}>
                          <LocationOnIcon sx={{ color: "text.secondary" }} />
                        </Grid>
                        <Grid
                          item
                          sx={{
                            width: "calc(100% - 44px)",
                            wordWrap: "break-word",
                          }}
                        >
                          {parts.map((part, index) => (
                            <Box
                              key={index}
                              component="span"
                              sx={{
                                fontWeight: part.highlight ? "bold" : "regular",
                              }}
                            >
                              {part.text}
                            </Box>
                          ))}
                          <Typography
                            variant="body2"
                            sx={{ color: "text.secondary" }}
                          >
                            {secondaryText}
                          </Typography>
                        </Grid>
                      </Grid>
                    </li>
                  );
                }}
              />
            </DialogContent>
          </Dialog>
        </>
      ) : (
        <Autocomplete
          autoFocus
          open={open} // Control the open state
          onOpen={() => setOpen(true)} // Open the list when the component gains focus
          onClose={() => setOpen(false)} // Close the list when clicking outside
          sx={{
            width: "100%",
            marginBottom: "16px",
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
            },
          }}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : option.description
          }
          filterOptions={(x) => x}
          options={options}
          autoComplete
          includeInputInList
          filterSelectedOptions
          value={value}
          noOptionsText={t("noLocations")}
          onChange={(event, newValue) => handleChange(newValue)}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              onKeyDown={handleKeyDown} // Move the onKeyDown handler here
              label={label}
              fullWidth
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <LocationOnIcon sx={{ color: "action.active", mr: 1 }} />
                ),
              }}
            />
          )}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;

            const structuredFormatting = option.structured_formatting || {};
            const mainText = structuredFormatting.main_text || "";
            const secondaryText = structuredFormatting.secondary_text || "";
            const matches =
              structuredFormatting.main_text_matched_substrings || [];

            const parts = parse(
              mainText,
              matches.map((match) => [
                match.offset,
                match.offset + match.length,
              ])
            );

            return (
              <li key={key} {...optionProps}>
                <Grid container alignItems="center">
                  <Grid item sx={{ display: "flex", width: 44 }}>
                    <LocationOnIcon sx={{ color: "text.secondary" }} />
                  </Grid>
                  <Grid
                    item
                    sx={{ width: "calc(100% - 44px)", wordWrap: "break-word" }}
                  >
                    {parts.map((part, index) => (
                      <Box
                        key={index}
                        component="span"
                        sx={{ fontWeight: part.highlight ? "bold" : "regular" }}
                      >
                        {part.text}
                      </Box>
                    ))}
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      {secondaryText}
                    </Typography>
                  </Grid>
                </Grid>
              </li>
            );
          }}
        />
      )}
    </>
  );
}
